import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import { getPostBySlug, getAllPosts, type BlogPost } from '../../../data/blogPosts';

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (!slug) {
      navigate('/blog');
      return;
    }

    const foundPost = getPostBySlug(slug);
    
    if (!foundPost) {
      navigate('/blog');
      return;
    }

    setPost(foundPost);

    // Obtener artículos relacionados de la misma categoría
    const allPosts = getAllPosts();
    const related = allPosts
      .filter(p => p.category === foundPost.category && p.slug !== foundPost.slug)
      .slice(0, 3);
    setRelatedPosts(related);
  }, [slug, navigate]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar scrolled={true} />
        <div className="pt-32 pb-20 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      {/* Hero del Artículo */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            <Link to="/" className="hover:text-cyan-400 transition-colors">Inicio</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/blog" className="hover:text-cyan-400 transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900">{post.title}</span>
          </div>

          {/* Categoría */}
          <div className="mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-full text-sm font-medium">
              <i className="ri-price-tag-3-line"></i>
              {post.category}
            </span>
          </div>

          {/* Título */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Meta información */}
          <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-cyan-500 flex items-center justify-center">
                <i className="ri-robot-line text-white"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{post.author}</p>
                <p className="text-xs text-gray-500">Contenido generado por IA</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-calendar-line text-cyan-400"></i>
              <span className="text-sm">{post.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-cyan-400"></i>
              <span className="text-sm">{post.readTime} de lectura</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-eye-line text-cyan-400"></i>
              <span className="text-sm">{post.views} vistas</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-chat-3-line text-cyan-400"></i>
              <span className="text-sm">{post.comments} comentarios</span>
            </div>
          </div>

          {/* Imagen destacada */}
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-[400px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Contenido del Artículo */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Contenido Principal */}
            <article className="lg:col-span-8">
              {/* Excerpt */}
              <div className="bg-gray-50 border-l-4 border-cyan-400 p-6 rounded-r-xl mb-8">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {post.excerpt}
                </p>
              </div>

              {/* Contenido del artículo */}
              <div 
                className="prose prose-lg max-w-none
                  prose-headings:font-bold prose-headings:text-gray-900
                  prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:text-cyan-500
                  prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-gray-800
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                  prose-ul:my-6 prose-ul:space-y-2
                  prose-li:text-gray-700
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-a:text-cyan-500 prose-a:no-underline hover:prose-a:text-cyan-600"
                dangerouslySetInnerHTML={{ __html: post.content || post.excerpt }}
              />

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Etiquetas</h3>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-cyan-500 hover:text-white transition-all duration-300 cursor-pointer"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Compartir */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Compartir artículo</h3>
                <div className="flex gap-3">
                  <button className="w-12 h-12 rounded-full bg-cyan-500 text-white flex items-center justify-center hover:shadow-lg hover:scale-110 transition-all duration-300">
                    <i className="ri-twitter-x-line text-lg"></i>
                  </button>
                  <button className="w-12 h-12 rounded-full bg-cyan-500 text-white flex items-center justify-center hover:shadow-lg hover:scale-110 transition-all duration-300">
                    <i className="ri-linkedin-fill text-lg"></i>
                  </button>
                  <button className="w-12 h-12 rounded-full bg-cyan-500 text-white flex items-center justify-center hover:shadow-lg hover:scale-110 transition-all duration-300">
                    <i className="ri-facebook-fill text-lg"></i>
                  </button>
                  <button className="w-12 h-12 rounded-full bg-cyan-500 text-white flex items-center justify-center hover:shadow-lg hover:scale-110 transition-all duration-300">
                    <i className="ri-whatsapp-line text-lg"></i>
                  </button>
                  <button className="w-12 h-12 rounded-full bg-cyan-500 text-white flex items-center justify-center hover:shadow-lg hover:scale-110 transition-all duration-300">
                    <i className="ri-link text-lg"></i>
                  </button>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              {/* Newsletter */}
              <div className="bg-gray-50 rounded-2xl p-6 mb-8 sticky top-24">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-mail-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Newsletter SEO</h3>
                  <p className="text-sm text-gray-600">Recibe las últimas tendencias SEO directamente en tu email</p>
                </div>
                <form className="space-y-3">
                  <input 
                    type="email" 
                    placeholder="Tu email"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm"
                  />
                  <button 
                    type="submit"
                    className="w-full px-6 py-3 bg-cyan-500 text-white rounded-xl font-medium hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap"
                  >
                    Suscribirme
                  </button>
                </form>
              </div>

              {/* Artículos relacionados */}
              {relatedPosts.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Artículos Relacionados</h3>
                  <div className="space-y-6">
                    {relatedPosts.map((relatedPost, index) => (
                      <Link 
                        key={index}
                        to={`/blog/${relatedPost.slug}`}
                        className="block group"
                      >
                        <div className="flex gap-4">
                          <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                            <img 
                              src={relatedPost.image} 
                              alt={relatedPost.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900 group-hover:text-cyan-400 transition-colors line-clamp-2 mb-2">
                              {relatedPost.title}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <i className="ri-calendar-line"></i>
                              <span>{relatedPost.date}</span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para llevar tu SEO al siguiente nivel?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Descubre cómo nuestra tecnología de IA puede transformar tu estrategia SEO
          </p>
          <Link 
            to="/#contacto"
            className="inline-block px-8 py-4 bg-cyan-500 text-white rounded-full font-medium text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
          >
            Solicitar Consulta Gratuita
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
